// 3rd Party
import _ from 'lodash';
import moment from 'moment';

// Other
import { SPECIALTY } from 'common/constants';

import { combineAllReviews, getOverallRating } from '../../common/helpers/common-helpers';
import {
  PROFILE_ACHIEVEMENT_SECTION_TYPES,
  PROFILE_ACHIEVEMENT_TYPES,
} from '../constants/constants';

const hasOpenHours = (clinicHours) =>
  clinicHours.find((hour) => hour.start && hour.end);

export const filterInNetworkProviders = (
  clinicHours,
  insurancePlanId,
  providers,
  {
    isPartnerUser = false,
  } = {},
) => {
  if (!providers?.length) {
    return [];
  }

  const inNetworkProviders = [];
  const outOfNetworkProviders = [];

  providers.forEach((provider) => {
    if (!provider.onlineBooking || !hasOpenHours(clinicHours)) {
      return;
    }

    const acceptedPlans = provider.doctor.insurancePlans;
    const isInNetwork = acceptedPlans.some((plan) => plan.id === insurancePlanId);

    if ((isInNetwork && provider.doctor.primaryPhoto.name) || isPartnerUser) {
      inNetworkProviders.push(provider);
    } else {
      outOfNetworkProviders.push(provider);
    }
  });

  // If no insurancePlanId is provided, all providers are out of network and therefore all providers are returned
  return inNetworkProviders.length === 0
    ? outOfNetworkProviders
    : inNetworkProviders;
};

const isDentistProvider = (specialties) =>
  _.some(specialties, {
    id: SPECIALTY.dentist,
  });

export const isOntarioDentistProvider = (clinicAddress, specialties) =>
  isDentistProvider(specialties)
  && clinicAddress.administrative_area_level_1 === 'Ontario';

const getProviderReviews = (providers) =>
  providers
  && providers.reduce(
    (reviews, provider) => reviews.concat(provider.doctor.reviews),
    [],
  );

// The most recent, over 120 character, non-external review comes first
// TODO do we prefer longer reviews?
const getReviewsByHelpfulness = (reviews) =>
  reviews
  && reviews
    .filter(
      (review) => !_.isEmpty(review.description) && getOverallRating(review) === 5,
    )
    .sort((a, b) => {
      if (
        moment.utc(a.approvedAt).valueOf()
        === moment.utc(b.approvedAt).valueOf()
      ) {
        return 0;
      }
      return moment.utc(a.approvedAt).valueOf()
        > moment.utc(b.approvedAt).valueOf()
        ? -1
        : 1;
    })
    .sort((a, b) => {
      if (!!a.externalSource === !!b.externalSource) {
        return 0;
      }
      return a.externalSource ? 1 : -1;
    })
    .sort((a, b) => {
      const { description: descriptionA = '' } = a;
      const { description: descriptionB = '' } = b;
      if (descriptionA.length === descriptionB.length) {
        return 0;
      }
      return descriptionA.length < descriptionB.length ? 1 : -1;
    })
    .sort((a, b) => {
      const { description: descriptionA = '' } = a;
      const { description: descriptionB = '' } = b;
      if (descriptionA.length === descriptionB.length) {
        return 0;
      }
      return a.description.length < 120 ? 1 : -1;
    });

// TODO: what if there are no clinic or provider reviews?
export const getMostHelpfulReview = (clinic) => {
  const reviews = combineAllReviews(clinic);

  const reviewsByHelpfulness = getReviewsByHelpfulness(reviews);
  if (reviewsByHelpfulness && reviewsByHelpfulness.length !== 0) {
    return reviewsByHelpfulness[0];
  }

  const providerReviewsByHelpfulness = getReviewsByHelpfulness(
    getProviderReviews(clinic.providers),
  );
  return providerReviewsByHelpfulness
    ? providerReviewsByHelpfulness[0]
    : undefined;
};

export const getProviderAchievements = (
  clinicAddress,
  onlineBookingEnabled,
  metadata,
  specialties,
  _title,
  type,
) => {
  const associations = [];
  const awards = [];
  if (
    isOntarioDentistProvider(clinicAddress, specialties)
    && onlineBookingEnabled
  ) {
    awards.push({
      name: "Patient's Choice Award Winner",
      type: PROFILE_ACHIEVEMENT_TYPES.awards,
    });
    awards.push({
      name: 'Opencare Certified Practice',
      type: PROFILE_ACHIEVEMENT_TYPES.awards,
    });
    associations.push({
      name: 'Canadian Dental Association',
      type: PROFILE_ACHIEVEMENT_TYPES.association,
    });
    const hasRCDSO = _.find(metadata.certificates, {
      name: 'Royal College of Dental Surgeons of Ontario',
    });
    if (!hasRCDSO) {
      associations.push({
        name: 'Royal College of Dental Surgeons of Ontario',
        type: PROFILE_ACHIEVEMENT_TYPES.association,
      });
    }
  }
  const certificatesAndEducation = [
    ..._.sortBy(metadata.education, 'year').map((education) => ({
      ...education,
      type: PROFILE_ACHIEVEMENT_TYPES.education,
    })),
    ..._.sortBy(metadata.certificates, 'date').map((certificates) => ({
      ...certificates,
      type: PROFILE_ACHIEVEMENT_TYPES.certification,
    })),
    ...associations,
  ];

  return type === PROFILE_ACHIEVEMENT_SECTION_TYPES.awards
    ? awards
    : certificatesAndEducation;
};

export const groupClinicReviews = (providers) => {
  const reviews = [];
  providers.forEach((provider) => {
    reviews.push(...provider.doctor.reviews);
  });
  return reviews;
};

export const groupProviderIds = (providers) => {
  const ids = [];
  providers.forEach((provider) => {
    ids.push(provider.doctor.id);
  });
  return ids;
};

export const getReviewStats = (ids, reviews) => {
  // TODO: consider using reselect for this
  const empty = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, total: 0 };
  let stats;
  if (ids.length > 1) {
    stats = ids.reduce((arr, id) => {
      if (arr[id] === undefined) {
        arr[id] = Object.assign({}, empty);
      }
      return arr;
    }, {});
  } else {
    stats = {};
    stats[ids] = Object.assign({}, empty);
  }
  stats.totals = Object.assign({}, empty);
  reviews.forEach((review) => {
    const rating = getOverallRating(review);
    if (stats[review.modelId] === undefined) {
      // Include clinic level reviews in total stats
      stats.totals[rating] += 1;
      stats.totals.total += 1;
    } else {
      // Provider level stats
      stats[review.modelId][rating] += 1;
      stats[review.modelId].total += 1;
      stats.totals[rating] += 1;
      stats.totals.total += 1;
    }
  });
  return stats;
};
